import React, { useEffect, useState } from "react";
import App from "../App";
import Cookies from "js-cookie";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Login from "../pages/Login";
import axios from "axios";
import { crmHeaders } from "../utils/crmApi";
import {
    canViewLeadsForCountry,
    canUseEmailForCountry,
    getVisibleCountries,
    hasFullAccess,
    canUseEmailSender,
    canManageFollowUps,
    canUseEmailValidator,
    canCreateEmailAccounts,
    hasAnyEmailAccess,
    canSendSms,
    canReceiveSms,
    hasAnySmsAccess,
    saveUserAccessCookie,
    saveUserPermissionsCookie,
} from "../utils/userAccess";
import { COUNTRY_RESOURCE_MENU } from "../utils/countryResourcesConfig";
import AiChatWidget from "./AiChatWidget";
import TeamChatWidget from "./TeamChatWidget";
import { EmailNotificationProvider, EmailCountBadge, useEmailNotifications } from "../context/EmailNotificationContext";
import { EmailHealthProvider, useEmailHealth } from "../context/EmailHealthContext";
import { TeamChatProvider } from "../context/TeamChatContext";
import { ThemeProvider, useTheme } from "../context/ThemeContext";
import { ToastContainer } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

function getUserInitials(name) {
    if (!name) return "U";
    return name.substring(0, 2).toUpperCase();
}

function navClass(isActive) {
    return `nav-link ${isActive ? "active" : ""}`;
}

function SidebarNav() {
    const getUsername = Cookies.get("username");
    const { byCountry, totalUnread } = useEmailNotifications();
    const { issueCount, hasCountryIssue, countryHasBlocked } = useEmailHealth();
    const { darkMode, toggleDarkMode, sidebarHidden, showSidebar } = useTheme();
    const getRole = Cookies.get("user_role");
    const navigate = useNavigate();
    const location = useLocation();
    const isMailPage =
        location.pathname.startsWith("/incoming-emails") ||
        location.pathname === "/emails/received-emails";

    const [countries, setCountries] = useState([]);
    const [accessSynced, setAccessSynced] = useState(false);

    useEffect(() => {
        axios
            .get(`${BASE_URL}/getcountries`, { headers: crmHeaders() })
            .then((res) => setCountries(res.data))
            .catch((error) => console.log("There was an error", error));
    }, []);

    useEffect(() => {
        const username = Cookies.get("username");
        if (!username) {
            setAccessSynced(true);
            return;
        }

        axios
            .get(`${BASE_URL}/my-country-access`, { headers: crmHeaders() })
            .then((res) => {
                const date = new Date();
                date.setTime(date.getTime() + 48 * 60 * 60 * 1000);
                if (res.data?.countryAccess !== undefined) {
                    saveUserAccessCookie(res.data.countryAccess, date);
                }
                if (res.data?.userPermissions) {
                    saveUserPermissionsCookie(res.data.userPermissions, date);
                }
            })
            .catch((error) => console.log("Access sync error:", error))
            .finally(() => setAccessSynced(true));
    }, []);

    useEffect(() => {
        if (!isMailPage) {
            showSidebar();
        }
    }, [isMailPage, showSidebar]);

    const logout = () => {
        axios
            .post(`${BASE_URL}/logout`, [], { headers: crmHeaders() })
            .then(() => {
                Cookies.remove("username");
                Cookies.remove("user_role");
                Cookies.remove("user_access");
                navigate("/");
            })
            .catch((error) => console.error(error));
    };

    const activeCountries = countries.filter((c) => c.Status === "Active");
    const visibleCountries = getVisibleCountries(activeCountries);
    const isAdmin = hasFullAccess();

    const isActive = (path) => location.pathname === path;

    const showEmailSender = canUseEmailSender();
    const showCreateEmail = canCreateEmailAccounts();
    const showValidator = hasFullAccess() || canUseEmailValidator();
    const showFollowUps = canManageFollowUps();
    const showAllIncoming = isAdmin || hasAnyEmailAccess();
    const showSmsSend = canSendSms();
    const showSmsReceive = canReceiveSms();
    const showSmsSection = isAdmin || hasAnySmsAccess();
    const showEmailToolsSection =
        showEmailSender || showCreateEmail || showValidator || showFollowUps;

    if (!accessSynced) {
        return (
            <div className="app-shell">
                <main className="app-main">
                    <div className="app-main-inner d-flex align-items-center justify-content-center">
                        <p className="text-muted mb-0">Loading your access...</p>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div>
            {getUsername ? (
                <div className={`app-shell ${sidebarHidden ? "app-shell--sidebar-hidden" : ""}`}>
                    <aside className="app-sidebar">
                        <Link to="/" className="app-sidebar-brand">
                            <div className="app-sidebar-brand-icon">
                                <i className="bi bi-grid-1x2-fill"></i>
                            </div>
                            <div>
                                <span className="app-sidebar-brand-text">CRM Portal</span>
                                <span className="app-sidebar-brand-sub">Management System</span>
                            </div>
                        </Link>

                        <nav className="app-sidebar-nav">
                            <ul className="nav flex-column w-100" id="menu">
                                <li className="nav-item">
                                    <Link to="/" className={navClass(isActive("/"))}>
                                        <i className="bi bi-house-door"></i>
                                        <span>Dashboard</span>
                                    </Link>
                                </li>

                                {visibleCountries.length > 0 && (
                                    <>
                                        <div className="app-sidebar-section">Countries</div>
                                        {visibleCountries.map((country) => {
                                            const encodedCountry = encodeURIComponent(country.Country);
                                            const mailPath = `/incoming-emails/${encodedCountry}`;
                                            const resourceBase = `/resources/${encodedCountry}/`;
                                            const showLeads = canViewLeadsForCountry(country.Country);
                                            const showInbox = canUseEmailForCountry(country.Country);
                                            const leadsPath = `/leads/${country.Country}`;
                                            const isCountryOpen =
                                                location.pathname === leadsPath ||
                                                location.pathname === mailPath ||
                                                location.pathname.startsWith(resourceBase);

                                            return (
                                                <li className="w-100" key={country.Id}>
                                                    <a
                                                        href={`#country-${country.Id}`}
                                                        data-bs-toggle="collapse"
                                                        className={`nav-link ${isCountryOpen ? "active" : ""}`}
                                                    >
                                                        <i className="bi bi-globe2"></i>
                                                        <span>{country.Country}</span>
                                                        <i className="bi bi-chevron-down ms-auto small"></i>
                                                    </a>
                                                    <ul
                                                        className={`collapse nav flex-column app-sidebar-submenu ${isCountryOpen ? "show" : ""}`}
                                                        id={`country-${country.Id}`}
                                                    >
                                                        {showLeads && (
                                                            <li>
                                                                <Link
                                                                    to={leadsPath}
                                                                    className={navClass(isActive(leadsPath))}
                                                                >
                                                                    <i className="bi bi-people"></i>
                                                                    <span>Leads</span>
                                                                </Link>
                                                            </li>
                                                        )}
                                                        {showInbox && (
                                                            <li>
                                                                <Link
                                                                    to={mailPath}
                                                                    className={navClass(isActive(mailPath))}
                                                                >
                                                                    <i className="bi bi-envelope"></i>
                                                                    <span>Emails</span>
                                                                    <EmailCountBadge count={byCountry[country.Country]} />
                                                                    {hasCountryIssue(country.Country) && (
                                                                        <i
                                                                            className={`bi bi-exclamation-triangle-fill ms-1 small ${
                                                                                countryHasBlocked(country.Country)
                                                                                    ? "text-danger"
                                                                                    : "text-warning"
                                                                            }`}
                                                                            title="Is country ki email account par issue hai"
                                                                            aria-hidden="true"
                                                                        />
                                                                    )}
                                                                </Link>
                                                            </li>
                                                        )}
                                                        {COUNTRY_RESOURCE_MENU.map((item) => {
                                                            const itemPath = `/resources/${encodedCountry}/${item.type}`;
                                                            return (
                                                                <li key={item.type}>
                                                                    <Link
                                                                        to={itemPath}
                                                                        className={navClass(isActive(itemPath))}
                                                                    >
                                                                        <i className={`bi ${item.icon}`}></i>
                                                                        <span>{item.label}</span>
                                                                    </Link>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </li>
                                            );
                                        })}
                                    </>
                                )}

                                {showAllIncoming && (
                                    <>
                                        <div className="app-sidebar-section">Inbox</div>
                                        <li>
                                            <Link
                                                to="/incoming-emails"
                                                className={navClass(location.pathname === "/incoming-emails")}
                                            >
                                                <i className="bi bi-inbox-fill"></i>
                                                <span>All Emails</span>
                                                <EmailCountBadge count={totalUnread} />
                                                {issueCount > 0 && (
                                                    <i
                                                        className="bi bi-exclamation-triangle-fill ms-1 small text-danger"
                                                        title="Aap ki email account(s) par issue hai"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </Link>
                                        </li>
                                    </>
                                )}

                                {showEmailToolsSection && (
                                    <>
                                        <div className="app-sidebar-section">Email Tools</div>
                                        {showEmailSender && (
                                            <li>
                                                <Link
                                                    to="/emails/email-sender"
                                                    className={navClass(isActive("/emails/email-sender"))}
                                                >
                                                    <i className="bi bi-send"></i>
                                                    <span>Send Email</span>
                                                </Link>
                                            </li>
                                        )}
                                        {showCreateEmail && (
                                            <li>
                                                <Link
                                                    to="/emails/addemail"
                                                    className={navClass(isActive("/emails/addemail"))}
                                                >
                                                    <i className="bi bi-envelope-plus"></i>
                                                    <span>Add Email Account</span>
                                                </Link>
                                            </li>
                                        )}
                                        {showValidator && (
                                            <li>
                                                <Link
                                                    to="/emails/validate-emails"
                                                    className={navClass(isActive("/emails/validate-emails"))}
                                                >
                                                    <i className="bi bi-shield-check"></i>
                                                    <span>Email Validator</span>
                                                </Link>
                                            </li>
                                        )}
                                        {showFollowUps && (
                                            <li>
                                                <Link
                                                    to="/follow-ups"
                                                    className={navClass(isActive("/follow-ups"))}
                                                >
                                                    <i className="bi bi-journal-check"></i>
                                                    <span>Follow-Ups</span>
                                                </Link>
                                            </li>
                                        )}
                                    </>
                                )}

                                {showSmsSection && (
                                    <>
                                        <div className="app-sidebar-section">SMS (Twilio)</div>
                                        {showSmsSend && (
                                            <li>
                                                <Link
                                                    to="/sms/send"
                                                    className={navClass(isActive("/sms/send"))}
                                                >
                                                    <i className="bi bi-chat-dots"></i>
                                                    <span>Send SMS</span>
                                                </Link>
                                            </li>
                                        )}
                                        {showSmsReceive && (
                                            <li>
                                                <Link
                                                    to="/sms/received"
                                                    className={navClass(isActive("/sms/received"))}
                                                >
                                                    <i className="bi bi-chat-left-text"></i>
                                                    <span>Received SMS</span>
                                                </Link>
                                            </li>
                                        )}
                                        {isAdmin && (
                                            <li>
                                                <Link
                                                    to="/sms"
                                                    className={navClass(
                                                        location.pathname.startsWith("/sms") &&
                                                            !isActive("/sms/send") &&
                                                            !isActive("/sms/received")
                                                    )}
                                                >
                                                    <i className="bi bi-gear"></i>
                                                    <span>SMS Settings</span>
                                                </Link>
                                            </li>
                                        )}
                                    </>
                                )}

                                {isAdmin && (
                                    <>
                                        <div className="app-sidebar-section">Administration</div>
                                        <li>
                                            <Link to="/uploadleads" className={navClass(isActive("/uploadleads"))}>
                                                <i className="bi bi-cloud-arrow-up"></i>
                                                <span>Upload Leads</span>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/countries" className={navClass(isActive("/countries"))}>
                                                <i className="bi bi-map"></i>
                                                <span>Countries</span>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/allleads" className={navClass(isActive("/allleads"))}>
                                                <i className="bi bi-database"></i>
                                                <span>All Data</span>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/users" className={navClass(isActive("/users"))}>
                                                <i className="bi bi-person-badge"></i>
                                                <span>Users</span>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/emails" className={navClass(location.pathname.startsWith("/emails") && !location.pathname.startsWith("/emails/email-sender") && !location.pathname.startsWith("/incoming-emails"))}>
                                                <i className="bi bi-envelope-at"></i>
                                                <span>Email Settings</span>
                                            </Link>
                                        </li>
                                    </>
                                )}
                            </ul>
                        </nav>

                        <div className="app-sidebar-footer">
                            <button
                                type="button"
                                className="app-sidebar-theme-toggle"
                                onClick={toggleDarkMode}
                                title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                            >
                                <i className={`bi ${darkMode ? "bi-sun-fill" : "bi-moon-fill"}`}></i>
                                <span>{darkMode ? "Light mode" : "Dark mode"}</span>
                            </button>
                            <div className="dropdown w-100">
                                <a
                                    href="/"
                                    className="app-sidebar-user dropdown-toggle w-100"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    onClick={(e) => e.preventDefault()}
                                >
                                    <div className="app-sidebar-avatar">{getUserInitials(getUsername)}</div>
                                    <div className="app-sidebar-user-info">
                                        <div className="app-sidebar-user-name">{getUsername}</div>
                                        <div className="app-sidebar-user-role">{getRole || "User"}</div>
                                    </div>
                                </a>
                                <ul className="dropdown-menu dropdown-menu-end shadow border-0">
                                    <li>
                                        <Link to="/updateprofile" className="dropdown-item">
                                            <i className="bi bi-person me-2"></i> Profile
                                        </Link>
                                    </li>
                                    <li><hr className="dropdown-divider" /></li>
                                    <li>
                                        <button type="button" className="dropdown-item text-danger" onClick={logout}>
                                            <i className="bi bi-box-arrow-right me-2"></i> Sign out
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </aside>

                    {sidebarHidden && (
                        <button
                            type="button"
                            className="app-sidebar-unhide-btn"
                            onClick={showSidebar}
                            title="Show sidebar menu"
                            aria-label="Show sidebar menu"
                        >
                            <i className="bi bi-layout-sidebar-inset"></i>
                        </button>
                    )}

                    <main className={`app-main ${isMailPage ? "app-main--flush" : ""}`}>
                        <div className={`app-main-inner ${isMailPage ? "app-main-inner--flush" : ""}`}>
                            <App />
                        </div>
                    </main>
                    <TeamChatWidget />
                    <AiChatWidget />
                </div>
            ) : (
                <Login />
            )}
        </div>
    );
}

export default function Sidebar() {
    return (
        <ThemeProvider>
            <EmailHealthProvider>
                <EmailNotificationProvider>
                    <TeamChatProvider>
                        <SidebarNav />
                        <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop />
                    </TeamChatProvider>
                </EmailNotificationProvider>
            </EmailHealthProvider>
        </ThemeProvider>
    );
}
